import React from 'react'

import {GetServerSideProps} from 'next'
import {NextSeo} from 'next-seo'
import useSWR from 'swr'
import urljoin from 'url-join'

import {WebPage} from 'src/components/layout'
import {WebPageCommonProps} from 'src/components/layout/WebPage'
import {SearchConsole} from 'src/components/products'
import OldProductVariantPage from 'src/components/products/ProductVariantPage'
import ProductVariantPage from 'src/components/products/redesign/productPage'
import {makePageCommonProps} from 'src/utils'

import {handleRestCache} from '@festi/common/api/global-cache'
import {
  restApi,
  Warehouse,
  FieldType,
  ProductType,
  VariantDetail,
  handleRestResponse,
  ProductDetail,
  Variant,
} from '@festi/common/api/rest'
import settings from '@festi/common/constants/settings'
import {getMetaDescription} from '@festi/common/utils/seo'
import {onDutyFree} from '@festi/common/constants/channels'
import {
  getProductBreadCrumbs,
  getProductBreadCrumbsNEW,
} from '@festi/common/utils/categories'
import {
  formatInventory,
  formatProperties,
  formatPropertiesNEW,
} from '@festi/common/utils/products'

interface PageParams {
  slug: string
  sku: string
}

const showNewDesign = settings.redesign

export const getServerSideProps: GetServerSideProps = async ({
  res,
  params,
  preview = false,
}) => {
  const {sku, slug} = params as unknown as PageParams

  const [
    //
    common,
    warehouses,
    fieldTypes,
    productTypes,
    initialProductVariant,
    initialVariant,
  ] = await Promise.all([
    makePageCommonProps(preview),
    handleRestCache('warehouses_list', restApi.warehousesList),
    handleRestCache('fieldtype_list', restApi.fieldTypesList), // FIXME: Should be part of common context
    handleRestCache('product_types_list', restApi.productTypesList),
    !showNewDesign
      ? handleRestResponse(restApi.productVariantsRetrieve(sku), true)
      : null,
    showNewDesign
      ? handleRestResponse(restApi.variantsRetrieve(sku), true)
      : null,
  ])

  const initialProduct = showNewDesign
    ? await handleRestResponse(
        restApi.productsRetrieve(initialVariant?.productId),
        true,
      )
    : null

  if (
    showNewDesign ? !initialVariant || !initialProduct : !initialProductVariant
  ) {
    return {
      notFound: true,
    }
  } else if (common.categories.length === 0) {
    res.setHeader('Cache-Control', 's-maxage=1, stale-while-revalidate')
  } else {
    res.setHeader('Cache-Control', 's-maxage=60, stale-while-revalidate=3600')
  }

  return {
    props: {
      id: initialVariant?.productId ?? null,
      sku,
      slug,
      common,
      preview,
      warehouses,
      fieldTypes,
      productTypes,
      initialProductVariant,
      initialVariant,
      initialProduct,
    },
  }
}

interface Props {
  id: string | null
  sku: string
  slug: string
  common: WebPageCommonProps
  warehouses: Warehouse[]
  fieldTypes: FieldType[]
  productTypes: ProductType[]
  initialProductVariant?: VariantDetail
  initialVariant?: Variant
  initialProduct?: ProductDetail
}

export default function VorurSku({
  id,
  sku,
  slug,
  common,
  fieldTypes,
  warehouses,
  productTypes,
  initialProductVariant,
  initialVariant,
  initialProduct,
}: Props): JSX.Element {
  return (
    <>
      {showNewDesign ? (
        <NewVorurSkuPage
          id={id}
          sku={sku}
          common={common}
          fieldTypes={fieldTypes}
          warehouses={warehouses}
          initialVariant={initialVariant}
          initialProduct={initialProduct}
        />
      ) : (
        <OldVorurSkuPage
          sku={sku}
          slug={slug}
          common={common}
          fieldTypes={fieldTypes}
          warehouses={warehouses}
          productTypes={productTypes}
          initialVariant={initialProductVariant}
        />
      )}
    </>
  )
}

interface OldPageProps {
  sku: string
  slug: string
  common: WebPageCommonProps
  warehouses: Warehouse[]
  fieldTypes: FieldType[]
  productTypes: ProductType[]
  initialVariant: VariantDetail
}

function OldVorurSkuPage({
  sku,
  slug,
  common,
  fieldTypes,
  warehouses,
  productTypes,
  initialVariant,
}: OldPageProps): JSX.Element {
  const {data: variant} = useSWR(
    [sku, 'variant'],
    ([sku]) => handleRestResponse(restApi.productVariantsRetrieve(sku)),
    {
      revalidateOnFocus: false,
      fallbackData: initialVariant,
    },
  )

  const breadCrumbs = getProductBreadCrumbs(common?.categories, variant)
  const properties = formatProperties(fieldTypes, variant)

  const {images, product} = variant
  const name = variant?.name || ''
  const shortDescription = getMetaDescription({ shortDescription: product?.shortDescription })

  const updatedInventory = formatInventory(
    variant.inventoryStatus,
    warehouses,
    product?.collectAtWarehouse,
  )

  const productType: ProductType = productTypes?.find(
    (type: ProductType) => type.slug === product?.type,
  )

  const activeProduct = !!variant?.listings?.[settings.channel]?.active
  const baseUrl = onDutyFree ? settings.dutyfreeUrl : settings.baseUrl

  return (
    <WebPage
      headTitle={name}
      backgroundColor="white"
      common={common}
      breadCrumbs={breadCrumbs}
    >
      {activeProduct && <SearchConsole variant={variant} />}

      <OldProductVariantPage
        variant={variant}
        inventory={updatedInventory}
        properties={properties}
        productType={productType}
      />

      <NextSeo
        title={name}
        description={shortDescription}
        canonical={urljoin(baseUrl, `/vorur/${slug}/${sku}`)}
        noindex={!activeProduct}
        nofollow={!activeProduct}
        openGraph={{
          title: name,
          description: shortDescription,
          images: [
            {
              url: images[0]?.image.productList,
              alt: name,
            },
          ],
        }}
      />
    </WebPage>
  )
}

interface NewPageProps {
  id: string
  sku: string
  common: WebPageCommonProps
  warehouses: Warehouse[]
  fieldTypes: FieldType[]
  initialVariant: Variant
  initialProduct: ProductDetail
}

function NewVorurSkuPage({
  id,
  sku,
  common,
  fieldTypes,
  warehouses,
  initialVariant,
  initialProduct,
}: NewPageProps): JSX.Element {
  const {data: product} = useSWR(
    [id, 'product'],
    ([id]) => handleRestResponse(restApi.productsRetrieve(Number(id))),
    {
      revalidateOnFocus: false,
      fallbackData: initialProduct,
    },
  )

  const {data: variant} = useSWR(
    [sku, 'variant'],
    ([sku]) => handleRestResponse(restApi.variantsRetrieve(sku)),
    {
      revalidateOnFocus: false,
      fallbackData: initialVariant,
    },
  )

  const breadCrumbs = getProductBreadCrumbsNEW(
    common?.categories,
    product,
    variant,
  )

  const properties = formatPropertiesNEW(fieldTypes, product, variant)

  const {images} = variant
  const name = variant?.name || ''
  const shortDescription = getMetaDescription({ shortDescription: product?.shortDescription, fallbackPrefix: `${name} hjá ` })

  const updatedInventory = formatInventory(
    variant.inventoryStatus,
    warehouses,
    product?.collectAtWarehouse,
  )

  const activeProduct = !!variant?.listings?.[settings.channel]?.active
  const baseUrl = onDutyFree ? settings.dutyfreeUrl : settings.baseUrl

  return (
    <WebPage
      headTitle={name}
      backgroundColor="white"
      common={common}
      breadCrumbs={breadCrumbs}
      hasBackButton
    >
      {activeProduct && <SearchConsole product={product} variant={variant} />}

      <ProductVariantPage
        product={product}
        variant={variant}
        inventory={updatedInventory}
        properties={properties}
      />

      <NextSeo
        title={name}
        description={shortDescription}
        canonical={urljoin(baseUrl, `/vorur/${product.slug}/${sku}`)}
        noindex={!activeProduct}
        nofollow={!activeProduct}
        openGraph={{
          title: name,
          description: shortDescription,
          images: [
            {
              url: images[0]?.image.productList,
              alt: name,
            },
          ],
        }}
      />
    </WebPage>
  )
}
